import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export interface UsePaginationProps {
  page: number;
  totalCount: number;
  size: number;
}

const usePagination = ({ page, totalCount, size }: UsePaginationProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const MAX_PAGE = Math.ceil(totalCount / size);
  const NUMBER_PER_PAGE = 5;

  // Calculate page range to display (5 pages per group, centered around current page when possible)
  let indexOfFirst: number;
  let indexOfLast: number;

  if (MAX_PAGE <= NUMBER_PER_PAGE) {
    // If total pages <= 5, show all pages
    indexOfFirst = 1;
    indexOfLast = MAX_PAGE;
  } else {
    // Calculate starting page: try to center around current page
    // Start from (current page - 2) to show current page in middle when possible
    indexOfFirst = Math.max(1, page - Math.floor(NUMBER_PER_PAGE / 2));
    
    // Adjust if we're near the end
    indexOfLast = Math.min(indexOfFirst + NUMBER_PER_PAGE - 1, MAX_PAGE);
    
    // Adjust if we're near the beginning (ensure we always show 5 pages if possible)
    if (indexOfLast - indexOfFirst < NUMBER_PER_PAGE - 1) {
      indexOfFirst = Math.max(1, indexOfLast - NUMBER_PER_PAGE + 1);
    }
  }

  // Validate to prevent invalid array length
  const arrayLength = Math.max(0, indexOfLast - indexOfFirst + 1);
  const pageNumberArray = arrayLength > 0 
    ? [...Array(arrayLength)].map((_, i) => indexOfFirst + i)
    : [];

  const handleNumberClick = (e: React.MouseEvent<HTMLElement>) => {
    const selectNumber = Number((e.target as HTMLElement).textContent);

    if (page === selectNumber) return;

    searchParams.set('page', String(selectNumber));
    setSearchParams(searchParams);
    navigate({ search: searchParams.toString() });
  };

  const handleRightArrowClick = () => {
    if (page >= MAX_PAGE) return;
    const nextPage = page + 1;
    searchParams.set('page', String(nextPage));
    setSearchParams(searchParams);
    navigate({ search: searchParams.toString() });
  };

  const handleLeftArrowClick = () => {
    if (page <= 1) return;
    const prevPage = page - 1;
    searchParams.set('page', String(prevPage));
    setSearchParams(searchParams);
    navigate({ search: searchParams.toString() });
  };

  const isFirstList = pageNumberArray.includes(1) && true;
  const isLastList = pageNumberArray.includes(MAX_PAGE) && true;

  return {
    pageNumberArray,
    handleNumberClick,
    handleRightArrowClick,
    handleLeftArrowClick,
    isFirstList,
    isLastList,
  };
};

export default usePagination;
